import { FormBuilder } from '@/components/formBuilder';
import { KButton, KText } from '@/ui-library/components';
import { Box, Flex } from '@chakra-ui/react';
import { useRef, useMemo, useState } from 'react';
import {
  buildCfIdToRowIdMap,
  generateOfferPayload,
} from '@/components/ats/OfferInitiation/offerFormHelpers';
import { offerDetailsAPI, useGetCustomFieldsQuery } from '../../api';
import { useOfferStore } from '../../store';
import { useShowToast } from '@/hooks';

interface EmptyTokensFormProps {
  nonFilledValidTokens: string[];
  detailsFormConfig: any[];
  currentOffer: any;
  applicationId: string | number;
  existingFormValues?: Record<string, any>;
  onCancel: () => void;
  onSaveSuccess: (_values?: Record<string, any>) => void;
}

export const EmptyTokensForm = ({
  nonFilledValidTokens,
  detailsFormConfig,
  currentOffer,
  applicationId,
  existingFormValues = {},
  onCancel,
  onSaveSuccess,
}: EmptyTokensFormProps) => {
  const formRef = useRef(null);
  const toast = useShowToast();
  const [isSaving, setIsSaving] = useState(false);

  // Read the same cached custom-fields query the drawer already fetched so
  // generateOfferPayload can translate Formik cf-id keys back to the row ids BE
  // expects. Without it required CFs 422 with err_processing_form_data (ENG-3357).
  const { data: customFieldsData } = useGetCustomFieldsQuery({
    subject_type: 'ats_offer',
  });
  // Use the scoped fields — the unscoped fetch omits entity-scoped ones, which would 422 on save.
  const scopedFormFields = useOfferStore((state) => state.scopedFormFields);
  const cfIdToRowIdMap = useMemo(
    () =>
      buildCfIdToRowIdMap(
        scopedFormFields?.length
          ? scopedFormFields
          : customFieldsData?.ats_form_fields
      ),
    [scopedFormFields, customFieldsData]
  );

  // Force every template token field required regardless of base config (CORE-4810).
  const missingFieldsConfig = useMemo(() => {
    const tokenSet = new Set(nonFilledValidTokens);
    return detailsFormConfig
      .flatMap((section) => section?.fields ?? [section])
      .filter((field) => field?.tokenName && tokenSet.has(field.tokenName))
      .map((field) => ({ ...field, isRequired: true }));
  }, [nonFilledValidTokens, detailsFormConfig]);

  const initialValues = useMemo(() => {
    const missingValues = missingFieldsConfig.reduce(
      (acc, field) => {
        acc[field.name] = currentOffer?.[field.name] ?? '';
        return acc;
      },
      {} as Record<string, any>
    );
    // Spread existingFormValues first so conditional-dependency fields (e.g.
    // award_type) are present; missing field values take precedence.
    return { ...existingFormValues, ...missingValues };
  }, [missingFieldsConfig, currentOffer, existingFormValues]);

  const handleSave = async () => {
    const form = formRef.current;
    if (!form) return;

    // Block save only when a token field is still empty; check values directly since the
    // forced-required schema false-positives on filled date/select/currency fields (CORE-4810).
    const isEmptyValue = (value: unknown) =>
      value === '' ||
      value === null ||
      value === undefined ||
      (Array.isArray(value) && value.length === 0);
    const stillEmpty = missingFieldsConfig.filter((field) =>
      isEmptyValue(form.values?.[field.name])
    );
    if (stillEmpty.length > 0) {
      form.setTouched(
        stillEmpty.reduce(
          (acc, field) => ({ ...acc, [field.name]: true }),
          {} as Record<string, boolean>
        )
      );
      return;
    }

    const newValues = form.values;

    // Merge existing offer form values with the newly filled token fields so
    // the update doesn't wipe fields that were already saved on the offer.
    // Fall back to the offer's linked requisition code if it's not in existingFormValues
    // (post-approval send flow where req feature is on may require it in the payload).
    const requisitionFallback = !existingFormValues?.requisitions_codes
      ? { requisitions_codes: currentOffer?.requisition?.code }
      : {};
    const values = {
      ...requisitionFallback,
      ...existingFormValues,
      ...newValues,
    };

    setIsSaving(true);
    try {
      const offerPayload = generateOfferPayload({
        values,
        applicationId,
        cfIdToRowIdMap,
      });
      const response = (await offerDetailsAPI.updateOffer({
        payload: offerPayload,
        id: currentOffer?.id,
      })) as any;

      if (response?.errors?.length > 0) {
        toast({
          title: 'Failed to save offer fields',
          status: 'error',
        });
        return;
      }

      // Seed offerPayload with ONLY the token fields this form collected — not the
      // full defaults-laden offer values. Seeding the whole set made a later template
      // switch read its default-backed required fields as already filled, so validation
      // didn't re-prompt and the doc generated without them (CORE-4822).
      const savedTokenValues = missingFieldsConfig.reduce(
        (acc, field) => {
          acc[field.name] = values[field.name];
          return acc;
        },
        {} as Record<string, any>
      );
      onSaveSuccess(savedTokenValues);
    } catch {
      toast({ title: 'Failed to save offer fields', status: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box p="24px" mt="12px">
      <KText size="md-semi-bold" color="secondary-900" mb="4px">
        Empty tokens detected in the offer letter template
      </KText>
      <KText size="sm-regular" color="secondary-600" mb="16px">
        Fill the missing information to generate the offer letter
      </KText>

      <FormBuilder
        initialValues={initialValues}
        config={missingFieldsConfig}
        formRef={formRef}
      />

      <Flex justifyContent="flex-end" gap="8px" mt="16px">
        <KButton variant="secondary" onClick={onCancel} isDisabled={isSaving}>
          Cancel
        </KButton>
        <KButton variant="primary" onClick={handleSave} isLoading={isSaving}>
          Save & generate offer
        </KButton>
      </Flex>
    </Box>
  );
};
